/**
 * The document session: one hook that owns "what am I working on, and is it safe".
 *
 * Keeping this out of the feature components is what stops React code from
 * issuing storage calls, and means the autosave rules — debounce, flush on hide,
 * never claim success early — exist in one place rather than per screen.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import type { OverrideMap } from '../ink/overrides';
import type { Stroke } from '../ink/types';
import type { StaffLayout } from '../theory/staff';
import { createAutosave, flushOnHide, type SaveStatus } from './autosave';
import { createDocument, emptyPayload, migrateDocument, UNTITLED } from './documents';
import { repositories } from './repositories';
import { fromRelativeAll, toRelativeAll } from './staffRelative';
import type { DocumentPayload, StoredDocument } from './types';
import { bumped } from './types';

/** Key under which the active document id is remembered. */
export const ACTIVE_DOCUMENT_KEY = 'activeDocument';

export interface DocumentSnapshot {
  clef: DocumentPayload['clef'];
  keyId: string;
  autoKey: boolean;
  strokes: Stroke[];
  overrides: OverrideMap;
  layout: StaffLayout | null;
}

export interface DocumentSession {
  document: StoredDocument | null;
  status: SaveStatus;
  /** True when writes are memory-only, so the UI can say so honestly. */
  ephemeral: boolean;
  ready: boolean;
  /** Note that the page changed. Debounced; safe to call on every stroke. */
  touch: () => void;
  flush: () => Promise<void>;
  rename: (title: string) => Promise<void>;
  openDocument: (id: string) => Promise<StoredDocument | null>;
  newDocument: () => Promise<StoredDocument>;
  restored: StoredDocument | null;
  acknowledgeRestore: () => void;
}

export interface UseDocumentSessionOptions {
  feature: string;
  /** Reads the current page. Called at save time, never during a stroke. */
  snapshot: () => DocumentSnapshot;
  /** Puts a loaded document back on the page. */
  apply: (payload: DocumentPayload, strokes: Stroke[]) => void;
  /** A better title once the app knows what was written. */
  suggestTitle?: () => string;
  delayMs?: number;
}

export function useDocumentSession(options: UseDocumentSessionOptions): DocumentSession {
  const repos = useMemo(() => repositories(), []);
  const [document, setDocument] = useState<StoredDocument | null>(null);
  const [status, setStatus] = useState<SaveStatus>('idle');
  const [ready, setReady] = useState(false);
  const [restored, setRestored] = useState<StoredDocument | null>(null);

  const documentRef = useRef<StoredDocument | null>(null);
  documentRef.current = document;
  const optionsRef = useRef(options);
  optionsRef.current = options;

  const write = useCallback(async () => {
    const current = documentRef.current;
    if (!current) return;
    const snapshot = optionsRef.current.snapshot();
    // Without a layout the coordinates are meaningless, so there is nothing
    // worth writing yet.
    if (!snapshot.layout) return;

    const payload: DocumentPayload = {
      clef: snapshot.clef,
      keyId: snapshot.keyId,
      autoKey: snapshot.autoKey,
      strokes: toRelativeAll(snapshot.strokes, snapshot.layout),
      overrides: snapshot.overrides,
    };
    const title =
      current.title === UNTITLED && optionsRef.current.suggestTitle
        ? optionsRef.current.suggestTitle()
        : current.title;

    const next = { ...bumped(current), title, payload };
    await repos.documents.put(next);
    documentRef.current = next;
    setDocument(next);
  }, [repos]);

  const autosave = useMemo(
    () =>
      createAutosave({
        save: write,
        delayMs: options.delayMs ?? 500,
        onStatus: (next) => setStatus(next),
      }),
    // Rebuilding this would drop a pending write, so it is created once.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  useEffect(() => () => autosave.dispose(), [autosave]);

  // Swiping the app away is the moment work is most likely to be lost, and iOS
  // gives no warning beyond these two events.
  useEffect(() => flushOnHide(() => void autosave.flush()), [autosave]);

  // -- open the last document, or start a new one ------------------------
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const pointer = await repos.featureState.get(ACTIVE_DOCUMENT_KEY);
        const lastId = typeof pointer?.value?.id === 'string' ? pointer.value.id : null;
        const existing = lastId ? await repos.documents.get(lastId) : null;
        const usable = existing && existing.deletedAt === null ? migrateDocument(existing) : null;

        if (cancelled) return;
        if (usable) {
          documentRef.current = usable;
          setDocument(usable);
          setRestored(usable);
          applyDocument(usable, optionsRef.current.apply);
        } else {
          const fresh = createDocument(optionsRef.current.feature, emptyPayload());
          await repos.documents.put(fresh);
          await repos.featureState.set(ACTIVE_DOCUMENT_KEY, { id: fresh.id });
          if (cancelled) return;
          documentRef.current = fresh;
          setDocument(fresh);
        }
      } catch {
        // Storage refused. The session still works, it just will not persist.
      } finally {
        if (!cancelled) setReady(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [repos]);

  const openDocument = useCallback(
    async (id: string) => {
      await autosave.flush();
      const raw = await repos.documents.get(id);
      const loaded = raw ? migrateDocument(raw) : null;
      if (!loaded) return null;
      documentRef.current = loaded;
      setDocument(loaded);
      setStatus('idle');
      await repos.featureState.set(ACTIVE_DOCUMENT_KEY, { id: loaded.id });
      applyDocument(loaded, optionsRef.current.apply);
      return loaded;
    },
    [autosave, repos],
  );

  const newDocument = useCallback(async () => {
    // Starting something new must never overwrite what came before it.
    await autosave.flush();
    const fresh = createDocument(optionsRef.current.feature, emptyPayload());
    await repos.documents.put(fresh);
    await repos.featureState.set(ACTIVE_DOCUMENT_KEY, { id: fresh.id });
    documentRef.current = fresh;
    setDocument(fresh);
    setStatus('idle');
    optionsRef.current.apply(fresh.payload, []);
    return fresh;
  }, [autosave, repos]);

  const rename = useCallback(
    async (title: string) => {
      const current = documentRef.current;
      if (!current) return;
      const next = { ...bumped(current), title: title.trim() || UNTITLED };
      await repos.documents.put(next);
      documentRef.current = next;
      setDocument(next);
    },
    [repos],
  );

  return {
    document,
    status,
    ephemeral: repos.ephemeral,
    ready,
    touch: autosave.schedule,
    flush: autosave.flush,
    rename,
    openDocument,
    newDocument,
    restored,
    acknowledgeRestore: () => setRestored(null),
  };
}

/**
 * Hand a loaded document to the page.
 *
 * Strokes stay in staff-relative units until the pad reports the layout it is
 * actually using, which is why `apply` receives them separately.
 */
function applyDocument(
  document: StoredDocument,
  apply: (payload: DocumentPayload, strokes: Stroke[]) => void,
): void {
  apply(document.payload, []);
}

/** Convert stored ink for the layout currently on screen. */
export function inkForLayout(payload: DocumentPayload, layout: StaffLayout): Stroke[] {
  return fromRelativeAll(payload.strokes, layout);
}
